/**
 * Oath Announcement Service
 * API calls for announcements — GET /oath/announcements, POST .../read
 */

import { OathStorageKeys } from '../models/userModels';
import { oathRefreshToken } from './oathAuthService';

const BASE_URL = process.env.REACT_APP_BASE_URL;

// ============================================
// Auth helpers (same pattern as oathNotificationService)
// ============================================

const getAuthHeaders = () => {
  const token = localStorage.getItem(OathStorageKeys.JWT_TOKEN);
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
};

const authFetch = async (url, options = {}) => {
  const response = await fetch(url, {
    ...options,
    headers: {
      ...getAuthHeaders(),
      ...options.headers,
    },
  });

  if (response.status === 401) {
    const refreshResult = await oathRefreshToken();
    if (refreshResult.success) {
      return fetch(url, {
        ...options,
        headers: {
          ...getAuthHeaders(),
          ...options.headers,
        },
      });
    }
  }

  return response;
};

// ============================================
// Service Functions
// ============================================

/**
 * Fetch announcements for the authenticated user.
 * Backend returns only active announcements, already filtered by state/agency.
 */
export const getAnnouncements = async () => {
  try {
    const response = await authFetch(`${BASE_URL}oath/announcements`);
    if (!response.ok) throw new Error('Failed to fetch announcements');
    const data = await response.json().catch(() => ({}));
    return {
      success: true,
      announcements: Array.isArray(data.announcements) ? data.announcements : [],
      unreadCount: data.unreadCount ?? 0,
    };
  } catch (error) {
    console.error('Error fetching announcements:', error);
    return { success: false, announcements: [], unreadCount: 0 };
  }
};

/**
 * Mark a single announcement as read. Pinned announcements will return 400.
 */
export const markAnnouncementRead = async (id) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/announcements/${id}/read`, {
      method: 'POST',
    });
    return { success: response.ok };
  } catch (error) {
    console.error('Error marking announcement as read:', error);
    return { success: false };
  }
};

/**
 * Bulk-mark all visible, non-pinned announcements as read.
 */
export const markAllAnnouncementsRead = async () => {
  try {
    const response = await authFetch(`${BASE_URL}oath/announcements/read-all`, {
      method: 'POST',
    });
    if (!response.ok) return { success: false };
    const data = await response.json().catch(() => ({}));
    return { success: true, markedCount: data.markedCount ?? 0 };
  } catch (error) {
    console.error('Error marking all announcements as read:', error);
    return { success: false };
  }
};
